import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Settings, Car, CalendarCheck, FileText, Bell, Users, Shield, Save } from 'lucide-react';
import { authHeaders } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONFIG_SCHEMA = [
  {
    group: "Access Control & Vehicles",
    icon: Car,
    color: "text-blue-400",
    fields: [
      { key: "max_cars_per_villa", label: "Max Cars per Villa", type: "number", description: "Maximum 4-wheelers a villa can register" },
      { key: "max_bikes_per_villa", label: "Max Bikes per Villa", type: "number", description: "Maximum 2-wheelers a villa can register" },
      { key: "vehicle_approval_required", label: "Vehicle Approval Required", type: "toggle", description: "Management must approve resident-added vehicles" },
      { key: "anpr_enabled", label: "ANPR Camera Integration", type: "toggle", description: "Enable automatic number plate recognition integration" },
    ]
  },
  {
    group: "Facility Booking",
    icon: CalendarCheck,
    color: "text-emerald-400",
    fields: [
      { key: "facility_booking_enabled", label: "Facility Booking", type: "toggle", description: "Enable facility booking module for residents" },
      { key: "booking_approval_required", label: "Booking Approval Required", type: "toggle", description: "Bookings need management approval before confirmation" },
      { key: "max_facilities", label: "Max Facilities", type: "number", description: "Maximum number of facilities that can be created" },
    ]
  },
  {
    group: "Invoicing & Payments",
    icon: FileText,
    color: "text-amber-400",
    fields: [
      { key: "invoicing_enabled", label: "Invoicing", type: "toggle", description: "Enable invoice generation module" },
      { key: "payments_enabled", label: "Payments", type: "toggle", description: "Enable online payment collection" },
      { key: "receipts_enabled", label: "Receipts", type: "toggle", description: "Enable receipt generation for payments" },
    ]
  },
  {
    group: "Communication",
    icon: Bell,
    color: "text-purple-400",
    fields: [
      { key: "notice_email_enabled", label: "Notice Emails", type: "toggle", description: "Send email notifications when notices are posted" },
      { key: "welcome_email_enabled", label: "Welcome Emails", type: "toggle", description: "Send welcome emails when users are created" },
    ]
  },
  {
    group: "Community Limits",
    icon: Users,
    color: "text-cyan-400",
    fields: [
      { key: "max_residents", label: "Max Residents", type: "number", description: "Maximum resident accounts allowed" },
      { key: "max_guards", label: "Max Guards", type: "number", description: "Maximum guard accounts allowed" },
    ]
  },
  {
    group: "Gate Pass",
    icon: Shield,
    color: "text-orange-400",
    fields: [
      { key: "gate_pass_enabled", label: "Gate Pass", type: "toggle", description: "Enable gate pass generation for visitors" },
      { key: "max_gate_pass_hours", label: "Max Gate Pass Validity (hours)", type: "number", description: "Maximum validity duration for a gate pass" },
    ]
  },
  {
    group: "Community Directory",
    icon: Users,
    color: "text-pink-400",
    fields: [
      { key: "mc_list_enabled", label: "Management Committee List", type: "toggle", description: "Show the MC members list (name, role, villa) to residents" },
      { key: "neighbors_directory_enabled", label: "Neighbors Directory", type: "toggle", description: "Allow residents to opt-in and view a directory of their neighbors" },
    ]
  },
  {
    group: "Helpdesk",
    icon: Users,
    color: "text-cyan-400",
    fields: [
      { key: "helpdesk_enabled", label: "Helpdesk / Tickets", type: "toggle", description: "Enable a tickets feature where residents can raise complaints/requests and management can respond" },
    ]
  }
];

function ToggleSwitch({ enabled, onChange, testId }) {
  return (
    <button
      data-testid={testId}
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gk-teal/50 ${enabled ? 'bg-gk-teal' : 'bg-slate-600'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

export function CommunityConfigPanel({ communityId, communityName }) {
  const [config, setConfig] = useState(null);
  const [localConfig, setLocalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!communityId) return;
    const fetchConfig = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/super-admin/communities/${communityId}/config`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          setConfig(data.config);
          setLocalConfig({ ...data.config });
          setHasChanges(false);
        }
      } catch (err) {
        toast.error('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [communityId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/super-admin/communities/${communityId}/config`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
        setLocalConfig({ ...data.config });
        setHasChanges(false);
      }
    } catch (err) {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    const updated = { ...localConfig, [key]: value };
    setLocalConfig(updated);
    const changed = Object.keys(updated).some(k => updated[k] !== config[k]);
    setHasChanges(changed);
  };

  const handleSave = async () => {
    const changedFields = {};
    for (const key of Object.keys(localConfig)) {
      if (localConfig[key] !== config[key]) {
        changedFields[key] = localConfig[key];
      }
    }
    if (Object.keys(changedFields).length === 0) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/super-admin/communities/${communityId}/config`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(changedFields)
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.config);
        setLocalConfig({ ...data.config });
        setHasChanges(false);
        toast.success('Configuration saved');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gk-teal"></div>
        <span className="ml-2 text-slate-400 text-sm">Loading configuration...</span>
      </div>
    );
  }

  if (!localConfig) return null;

  return (
    <div data-testid="community-config-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gk-teal flex items-center gap-1.5">
          <Settings className="w-4 h-4" />
          Feature Configuration
        </h4>
        {hasChanges && (
          <Button
            data-testid="save-config-button"
            onClick={handleSave}
            disabled={saving}
            size="sm"
            className="bg-gk-teal hover:bg-gk-teal-hover h-7 text-xs"
          >
            <Save className="w-3 h-3 mr-1" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </div>

      {CONFIG_SCHEMA.map((group) => {
        const GroupIcon = group.icon;
        return (
          <div key={group.group} className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50">
            <p className={`text-xs font-semibold ${group.color} flex items-center gap-1.5 mb-3`}>
              <GroupIcon className="w-3.5 h-3.5" />
              {group.group}
            </p>
            <div className="space-y-3">
              {group.fields.map((field) => (
                <div key={field.key} className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <Label className="text-slate-200 text-xs font-medium">{field.label}</Label>
                    <p className="text-[10px] text-slate-500 leading-tight">{field.description}</p>
                  </div>
                  {field.type === 'toggle' ? (
                    <ToggleSwitch
                      testId={`config-toggle-${field.key}`}
                      enabled={localConfig[field.key]}
                      onChange={(val) => handleChange(field.key, val)}
                    />
                  ) : (
                    <Input
                      data-testid={`config-input-${field.key}`}
                      type="number"
                      min="1"
                      value={localConfig[field.key]}
                      onChange={(e) => handleChange(field.key, parseInt(e.target.value) || 1)}
                      className="w-20 h-7 text-xs bg-slate-800 border-slate-600 text-white text-center"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {hasChanges && (
        <div className="flex items-center justify-between p-3 bg-amber-900/20 rounded-lg border border-amber-500/30">
          <p className="text-xs text-amber-300">You have unsaved changes</p>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setLocalConfig({ ...config }); setHasChanges(false); }} className="text-slate-400 h-7 text-xs">Discard</Button>
            <Button data-testid="save-config-bottom-button" size="sm" onClick={handleSave} disabled={saving} className="bg-gk-teal hover:bg-gk-teal-hover h-7 text-xs">
              <Save className="w-3 h-3 mr-1" />{saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
